import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography
} from '@mui/material';
import { translations } from '../translations/productSelectorTranslation';
import {
  ZERO,
  ONE,
  RADIX_DECIMAL,
  FIRST_ELEMENT_INDEX,
  DIALOG_HEIGHT_VH,
  TABLE_MAX_HEIGHT_PX,
  TABLE_MIN_HEIGHT_PX,
  MIN_WIDTH_SERIAL_SELECT,
  MIN_WIDTH_QUANTITY_CELL,
  WIDTH_QUANTITY_INPUT_XS,
  WIDTH_QUANTITY_INPUT_SM,
  SPIN_BUTTON_HEIGHT,
  COLOR_ERROR_BACKGROUND,
  COLOR_WARNING_BACKGROUND,
  TABLE_COLSPAN_WITH_SERIAL,
  TABLE_COLSPAN_WITHOUT_SERIAL,
  INPUT_TYPE_NUMBER,
  INPUT_SIZE_SMALL,
  VARIANT_OUTLINED,
  DIALOG_MAX_WIDTH,
  PRODUCT_FIELDS,
  EMPTY_STRING,
  UI_TEXT
} from '../constants/productSelectorConstants';

const ProductSelector = ({ products, onProductsChange, open, onClose, isLoadingProducts }) => {
  const [localSelectedProducts, setLocalSelectedProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState(EMPTY_STRING);
  const [partNumberSearchTerm, setPartNumberSearchTerm] = useState(EMPTY_STRING);
  const [promoProductSearchTerm, setPromoProductSearchTerm] = useState(EMPTY_STRING);

  const filteredProducts = products.filter((product) =>
    product[PRODUCT_FIELDS.PART_DESCRIPTION].toLowerCase().includes(searchTerm.toLowerCase()) &&
    product[PRODUCT_FIELDS.PART_NAME].toLowerCase().includes(partNumberSearchTerm.toLowerCase()) &&
    (promoProductSearchTerm === EMPTY_STRING ||
      (promoProductSearchTerm === translations.yes && product[PRODUCT_FIELDS.PROMO_EXAMPLE] !== null) ||
      (promoProductSearchTerm === translations.no && product[PRODUCT_FIELDS.PROMO_EXAMPLE] === null))
  );

  useEffect(() => {
    if (open) {
      console.log('ProductSelector - products from backend:', products);
      setLocalSelectedProducts(products.map(product => ({
        ...product,
        [PRODUCT_FIELDS.QUANTITY]: product[PRODUCT_FIELDS.QUANTITY] || ZERO
      })));
    }
  }, [open, products]);

  const handleQuantityChange = (partName, quantity, balance) => {
    setLocalSelectedProducts(prevProducts =>
      prevProducts.map(product => {
        if (product[PRODUCT_FIELDS.PART_NAME] === partName) {
          return {
            ...product,
            [PRODUCT_FIELDS.QUANTITY]: Math.max(ZERO, quantity),
            [PRODUCT_FIELDS.IS_OVER_LIMIT]: quantity > balance
          };
        }
        return product;
      })
    );
  };

  const handleSerialChange = (partName, serialValue) => {
    setLocalSelectedProducts(prevProducts =>
      prevProducts.map(product => {
        if (product[PRODUCT_FIELDS.PART_NAME] === partName) {
          return { ...product, [PRODUCT_FIELDS.SELECTED_SERIAL]: serialValue };
        }
        return product;
      })
    );
  };

  const handleConfirm = () => {
    onProductsChange(localSelectedProducts.filter(product => product[PRODUCT_FIELDS.QUANTITY] > ZERO));
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth={DIALOG_MAX_WIDTH} fullWidth>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', height: DIALOG_HEIGHT_VH, padding: 2 }}>
        <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
          <TextField
            label={translations.productSearch}
            variant={VARIANT_OUTLINED}
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <TextField
            label={translations.partNumberSearch}
            variant={VARIANT_OUTLINED}
            fullWidth
            value={partNumberSearchTerm}
            onChange={(e) => setPartNumberSearchTerm(e.target.value)}
          />
          <FormControl fullWidth variant={VARIANT_OUTLINED}>
            <InputLabel id="promo-product-label">{translations.promoProduct}</InputLabel>
            <Select
              labelId="promo-product-label"
              value={promoProductSearchTerm}
              onChange={(e) => setPromoProductSearchTerm(e.target.value)}
              label={translations.promoProduct}
            >
              <MenuItem value={EMPTY_STRING}>{translations.all}</MenuItem>
              <MenuItem value={translations.yes}>{translations.yes}</MenuItem>
              <MenuItem value={translations.no}>{translations.no}</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ height: TABLE_MAX_HEIGHT_PX, width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {isLoadingProducts ? (
            <CircularProgress />
          ) : (
            <TableContainer component={Paper} sx={{ flexGrow: 1, minHeight: TABLE_MIN_HEIGHT_PX, maxHeight: 'calc(100% - 80px)' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>{translations.product}</TableCell>
                    <TableCell>{translations.partNumber}</TableCell>
                    <TableCell>{translations.inventory}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{translations.promoProduct}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{translations.unitname}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{translations.conv}</TableCell>
                    <TableCell sx={{ minWidth: MIN_WIDTH_QUANTITY_CELL, textAlign: 'center' }}>{translations.quantity}</TableCell>
                    {localSelectedProducts && localSelectedProducts.some(p => p[PRODUCT_FIELDS.QUANTITY] > ZERO) && (
                      <TableCell sx={{ minWidth: MIN_WIDTH_QUANTITY_CELL, textAlign: 'center' }}>{translations.SERIAL}</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoadingProducts ? (
                    <TableRow>
                      <TableCell colSpan={localSelectedProducts && localSelectedProducts[PRODUCT_FIELDS.QUANTITY] > ZERO ? TABLE_COLSPAN_WITH_SERIAL : TABLE_COLSPAN_WITHOUT_SERIAL} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : filteredProducts && filteredProducts.length > ZERO ? (
                    filteredProducts.map((product) => {
                      const selectedProduct = localSelectedProducts && localSelectedProducts.find(p => p[PRODUCT_FIELDS.PART_NAME] === product[PRODUCT_FIELDS.PART_NAME]);
                      const productBalance = product[PRODUCT_FIELDS.LOG_COUNTERS_SUBFORM][FIRST_ELEMENT_INDEX][PRODUCT_FIELDS.BALANCE];
                      return (
                        <TableRow key={product[PRODUCT_FIELDS.PART_NAME]}
                          sx={{
                            backgroundColor: selectedProduct?.[PRODUCT_FIELDS.QUANTITY]
                              ? selectedProduct[PRODUCT_FIELDS.QUANTITY] > productBalance
                                ? COLOR_ERROR_BACKGROUND
                                : COLOR_WARNING_BACKGROUND
                              : 'inherit'
                          }}
                        >
                          <TableCell component="th" scope="row">
                            {product[PRODUCT_FIELDS.PART_DESCRIPTION]}
                          </TableCell>
                          <TableCell>{product[PRODUCT_FIELDS.PART_NAME]}</TableCell>
                          <TableCell>{productBalance}</TableCell>
                          <TableCell>{product[PRODUCT_FIELDS.PROMO_EXAMPLE] == null ? translations.no : translations.yes}</TableCell>
                          <TableCell>{product[PRODUCT_FIELDS.UNIT_NAME]}</TableCell>
                          <TableCell>{product[PRODUCT_FIELDS.CONVERSION]}</TableCell>
                          <TableCell>
                            <TextField
                              type={INPUT_TYPE_NUMBER}
                              size={INPUT_SIZE_SMALL}
                              value={selectedProduct ? selectedProduct[PRODUCT_FIELDS.QUANTITY] : ZERO}
                              onChange={(e) => handleQuantityChange(product[PRODUCT_FIELDS.PART_NAME], parseInt(e.target.value, RADIX_DECIMAL) || ZERO, productBalance)}
                              InputProps={{
                                inputProps: {
                                  min: ZERO,
                                  style: { textAlign: 'center' }
                                }
                              }}
                              error={selectedProduct && selectedProduct[PRODUCT_FIELDS.QUANTITY] > productBalance}
                              helperText={selectedProduct && selectedProduct[PRODUCT_FIELDS.QUANTITY] > productBalance ?
                                <Typography variant="caption" color="error" sx={{ fontSize: { xs: '10px', sm: '9px' } }}>
                                  {translations.quantityExceedsInventory}
                                </Typography>
                                : EMPTY_STRING
                              }
                              sx={{
                                width: {
                                  xs: WIDTH_QUANTITY_INPUT_XS,
                                  sm: WIDTH_QUANTITY_INPUT_SM
                                },
                                '& .MuiInputBase-input': {
                                  padding: {
                                    xs: '4px',
                                    sm: '8px'
                                  },
                                  fontSize: {
                                    xs: '14px',
                                    sm: '16px'
                                  }
                                },
                                '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
                                  opacity: 1,
                                  height: SPIN_BUTTON_HEIGHT
                                }
                              }}
                            />
                          </TableCell>
                          {selectedProduct && selectedProduct[PRODUCT_FIELDS.QUANTITY] > ZERO && (
                            <TableCell>
                              {product[PRODUCT_FIELDS.PART_BALANCE_SUBFORM] && product[PRODUCT_FIELDS.PART_BALANCE_SUBFORM].length > ZERO ? (
                                <FormControl size={INPUT_SIZE_SMALL} sx={{ minWidth: MIN_WIDTH_SERIAL_SELECT }}>
                                  <Select
                                    value={selectedProduct ? selectedProduct[PRODUCT_FIELDS.SELECTED_SERIAL] || EMPTY_STRING : EMPTY_STRING}
                                    onChange={(e) => handleSerialChange(product[PRODUCT_FIELDS.PART_NAME], e.target.value)}
                                    displayEmpty
                                  >
                                    <MenuItem value={EMPTY_STRING}>
                                      <em>{UI_TEXT.SELECT_BATCH}</em>
                                    </MenuItem>
                                    {product[PRODUCT_FIELDS.PART_BALANCE_SUBFORM].map((batch, index) => (
                                      <MenuItem key={index} value={batch[PRODUCT_FIELDS.SERIAL_NAME] || index}>
                                        {batch[PRODUCT_FIELDS.SERIAL_NAME] || `${UI_TEXT.BATCH_PREFIX} ${index + ONE}`}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              ) : (
                                <Typography variant="body2" color="textSecondary" align="center">
                                  {UI_TEXT.NO_VALUE_PLACEHOLDER}
                                </Typography>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={localSelectedProducts && localSelectedProducts[PRODUCT_FIELDS.QUANTITY] > ZERO ? TABLE_COLSPAN_WITH_SERIAL : TABLE_COLSPAN_WITHOUT_SERIAL} align="center">
                        <Typography>{translations.noProducts}</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{translations.cancel}</Button>
        <Button onClick={handleConfirm} variant="contained" color="primary" disabled={isLoadingProducts}>
          {translations.confirm}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductSelector;